/**
 * MetricExplanationContentView
 *
 * Pure presentation component for displaying metric explanation content.
 * Renders metric descriptions, methodology, and appropriate visualizations.
 * @remarks
 * This component handles the UI rendering of:
 * - Text descriptions with bold numbers
 * - Methodology sections
 * - DotPlot charts for standard metrics
 * - CategoryMetricChart for categorical metrics (carrying, dribbling, passing, etc.)
 */

import { SkFont } from "@shopify/react-native-skia";
import React from "react";

import {
  MetricExplanation,
  RosterDataState,
} from "@/types/metricExplanationTypes";

import CategoryMetricChart from "@/components/organisms/CategoryMetricChart";
import { CategoryMetricChartProps } from "@/components/organisms/CategoryMetricChart/CategoryMetricChartContainer";
import DotPlot from "@/components/molecules/DotPlot";
import { Box } from "@/components/atoms/Box";
import { Text } from "@/components/atoms/Text";
import { Stack } from "@/components/atoms/Stack";
import { Spinner } from "@/components/atoms/Spinner";
import { PlayerDetailPlayerMetaFragment } from "@/generated/graphql";

/**
 * Props for MetricExplanationContentView component
 */
interface MetricExplanationContentViewProps {
  explanation: MetricExplanation;
  fullDescription: string;
  hasScore: boolean;
  score: number;
  roundedRosterValues?: number[];
  rosterDataState: RosterDataState;
  player?: PlayerDetailPlayerMetaFragment;
  isDark: boolean;
  categoryChartData: {
    data: CategoryMetricChartProps["data"];
    yKeys: string[];
    colors: Record<string, string>;
  } | null;
  categoryChartDataState: RosterDataState;
  playerPositions?: string;
  font: SkFont | null;
  playerName: string;
  isSmallScreen: boolean;
}

/**
 * Renders each part of text as a Text component, with numbers styled in bold.
 * @param part - The text part to render
 * @param index - The array index for key generation
 * @param isDark - Whether dark theme is active
 * @returns A Text component with appropriate styling
 */
const renderPart = (
  part: string,
  index: number,
  isDark: boolean
): React.ReactElement => {
  const isNumber = /^\d+(?:\.\d+)?$/.test(part);
  const themeClass = isDark ? "text-white" : "text-content-primary";
  const className = isNumber ? themeClass : `text-title-sm ${themeClass}`;

  return (
    <Text
      key={`${part}-${index}`}
      variant={isNumber ? "title-sm" : "unstyled"}
      className={className}
    >
      {part}
    </Text>
  );
};

/**
 * Renders text with numbers styled in bold.
 * @param text - The text string to render with bold numbers
 * @param isDark - Whether dark theme is active
 * @returns A Fragment containing Text components
 */
function renderTextWithBoldNumbers(
  text: string,
  isDark: boolean
): React.ReactElement {
  const parts = text.split(/(\d+(?:\.\d+)?)/g);
  return <>{parts.map((part, index) => renderPart(part, index, isDark))}</>;
}

/**
 * Renders the explicit "no comparison data" state for a chart region.
 * @param testID - Which chart region the state belongs to.
 * @returns The empty-state element
 */
const renderEmptyChartState = (testID: string): React.ReactElement => (
  <Box className="pt-8" testID={testID} accessibilityLiveRegion="polite">
    <Stack className="items-center justify-center py-12">
      <Text variant="body" className="text-center text-content-muted">
        No roster comparison data available
      </Text>
    </Stack>
  </Box>
);

/**
 * Renders the explicit loading state for a chart region.
 * @param testID - Which chart region the state belongs to.
 * @returns The loading-state element
 */
const renderLoadingChartState = (testID: string): React.ReactElement => (
  <Box className="pt-8" testID={testID} accessibilityLiveRegion="polite">
    <Stack direction="horizontal" className="items-center justify-center py-12">
      <Spinner size="small" aria-label="Loading roster comparison" />
    </Stack>
  </Box>
);

/**
 * Arguments for {@link renderComparisonChartRegion}.
 */
interface ComparisonChartRegionArgs {
  rosterDataState: RosterDataState;
  roundedRosterValues?: number[];
  score: number;
  explanation: MetricExplanation;
  player?: PlayerDetailPlayerMetaFragment;
  isDark: boolean;
}

/**
 * Renders the roster-comparison chart region in exactly one explicit state.
 * @remarks
 * SE-7394: this region used to render nothing at all when roster values were
 * empty, which a user cannot tell apart from a metric that has no chart by
 * design. The `never` check below makes a future unhandled state a compile
 * error rather than a silently blank region, and a `ready` state that somehow
 * arrives without values falls through to the empty state rather than to
 * nothing.
 * @param args - The chart region arguments
 * @param args.rosterDataState - Which state the chart region should render
 * @param args.roundedRosterValues - Rounded roster peer values backing the DotPlot
 * @param args.score - Rounded metric score for the current player
 * @param args.explanation - Metric explanation, used for the DotPlot's metric id
 * @param args.player - Player data, used for the DotPlot's label
 * @param args.isDark - Whether dark theme is active
 * @returns The element for the current state, or null when the metric has no chart
 */
const renderComparisonChartRegion = ({
  rosterDataState,
  roundedRosterValues,
  score,
  explanation,
  player,
  isDark,
}: ComparisonChartRegionArgs): React.ReactElement | null => {
  switch (rosterDataState) {
    case "hidden":
      return null;
    case "loading":
      return renderLoadingChartState("metric-explanation-chart-loading");
    case "empty":
      return renderEmptyChartState("metric-explanation-chart-empty");
    case "ready":
      return roundedRosterValues ? (
        <Box className="pt-8" testID="metric-explanation-chart">
          <DotPlot
            currentValue={score}
            rosterValues={roundedRosterValues}
            lastName={player?.lastName || "This Player"}
            isDark={isDark}
            metricId={explanation.id}
          />
        </Box>
      ) : (
        renderEmptyChartState("metric-explanation-chart-empty")
      );
    default: {
      const exhaustiveCheck: never = rosterDataState;
      return exhaustiveCheck;
    }
  }
};

/**
 * Arguments for {@link renderCategoryChartRegion}.
 */
interface CategoryChartRegionArgs {
  categoryChartDataState: RosterDataState;
  categoryChartData: MetricExplanationContentViewProps["categoryChartData"];
  playerPositions?: string;
  font: SkFont | null;
  playerName: string;
  isSmallScreen: boolean;
}

/**
 * Renders the categorical over-time chart region in exactly one explicit state.
 * @remarks
 * SE-7449: this region is the categorical sibling of
 * {@link renderComparisonChartRegion}, and it had the same defect SE-7394 fixed
 * there — the chart was gated on `categoryChartData` being populated, so a
 * still-in-flight or genuinely empty peer query produced NO chart, NO skeleton
 * and NO empty state. `CategoryMetricChart`'s own `loading` prop could not
 * cover that: the gate deciding whether to mount it at all was false exactly
 * when loading was true, which left the affordance unreachable from here.
 *
 * The `loading` prop is therefore pinned to `false` in the `ready` case: this
 * switch owns the loading affordance now, and `ready` must keep winning over
 * `loading` so a background re-fetch cannot blank a chart the user is reading.
 * @param args - The chart region arguments
 * @param args.categoryChartDataState - Which state the chart region should render
 * @param args.categoryChartData - Series, keys and colors backing the chart
 * @param args.playerPositions - Comma-separated position abbreviations for the legend
 * @param args.font - Loaded Skia font for chart text rendering
 * @param args.playerName - Player name shown in the chart legend
 * @param args.isSmallScreen - Whether screen size is mobile (affects chart sizing)
 * @returns The element for the current state, or null when the metric has no category chart
 */
const renderCategoryChartRegion = ({
  categoryChartDataState,
  categoryChartData,
  playerPositions,
  font,
  playerName,
  isSmallScreen,
}: CategoryChartRegionArgs): React.ReactElement | null => {
  switch (categoryChartDataState) {
    case "hidden":
      return null;
    case "loading":
      return renderLoadingChartState(
        "metric-explanation-category-chart-loading"
      );
    case "empty":
      return renderEmptyChartState("metric-explanation-category-chart-empty");
    case "ready":
      return categoryChartData ? (
        <Box className="pt-8" testID="metric-explanation-category-chart">
          <CategoryMetricChart
            title="Over Time"
            hideBorder={false}
            hideTitle
            playerPositions={playerPositions}
            data={categoryChartData.data}
            yKeys={categoryChartData.yKeys}
            font={font}
            colors={categoryChartData.colors}
            loading={false}
            name={playerName}
            isSmallScreen={isSmallScreen}
          />
        </Box>
      ) : (
        renderEmptyChartState("metric-explanation-category-chart-empty")
      );
    default: {
      const exhaustiveCheck: never = categoryChartDataState;
      return exhaustiveCheck;
    }
  }
};

/**
 * View component for displaying metric explanation content
 * @param props - Component props
 * @param props.explanation - Metric explanation object containing title and methodology
 * @param props.fullDescription - Complete description text including score and team comparison
 * @param props.hasScore - Whether a valid numeric score is available to display.
 *   Not read directly by this view: it is folded into `rosterDataState`, which
 *   resolves to `hidden` when there is no score to plot.
 * @param props.score - Rounded metric score value
 * @param props.roundedRosterValues - Rounded array of roster values for DotPlot visualization
 * @param props.rosterDataState - Which of the four chart-region states to render (SE-7394)
 * @param props.player - Player data for chart rendering and name display
 * @param props.isDark - Whether dark theme is active (affects text and chart colors)
 * @param props.categoryChartData - Chart data for categorical metrics (carrying, dribbling, passing, etc.)
 * @param props.categoryChartDataState - Which of the four categorical chart-region states to render (SE-7449)
 * @param props.playerPositions - Comma-separated string of player position abbreviations
 * @param props.font - Loaded Skia font for chart text rendering
 * @param props.playerName - Full or last name of player for chart display
 * @param props.isSmallScreen - Whether screen size is mobile (affects chart sizing)
 */
const MetricExplanationContentView: React.FC<
  MetricExplanationContentViewProps
> = ({
  explanation,
  fullDescription,
  score,
  roundedRosterValues,
  rosterDataState,
  player,
  isDark,
  categoryChartData,
  categoryChartDataState,
  playerPositions,
  font,
  playerName,
  isSmallScreen,
}) => (
  <Stack className="gap-4">
    <Text
      variant="unstyled"
      className={`text-title-sm ${isDark ? "text-white" : "text-content-primary"}`}
    >
      {renderTextWithBoldNumbers(fullDescription, isDark)}
    </Text>

    {explanation.methodology && (
      <Stack className="gap-2">
        <Text
          variant="unstyled"
          className={`text-title-sm ${isDark ? "text-white" : "text-content-primary"}`}
        >
          {explanation.methodology}
        </Text>
      </Stack>
    )}

    {renderComparisonChartRegion({
      rosterDataState,
      roundedRosterValues,
      score,
      explanation,
      player,
      isDark,
    })}

    {renderCategoryChartRegion({
      categoryChartDataState,
      categoryChartData,
      playerPositions,
      font,
      playerName,
      isSmallScreen,
    })}
  </Stack>
);

MetricExplanationContentView.displayName = "MetricExplanationContentView";

export default React.memo(MetricExplanationContentView);
